import { useMemo } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { SecondarySidebar } from '../../../shared/components/SecondarySidebar/SecondarySidebar';
import { GenericPageShimmer } from '../../../shimmer/GenericPageShimmer/GenericPageShimmer';
import { RetryApiAlert } from '../../../shared/components/RetryApiAlert/RetryApiAlert';
import { EntityUIState } from '../../../request/interfaces/entity-ui-state';
import { getDataIdsFromContract } from '../../../shared/utils/dataId';
import { PipelineOverviewV1SetupWrapper } from './PipelineOverviewSetup/PipelineOverviewV1SetupWrapper';
import { PipelineHeader } from './PipelineHeader/PipelineHeader';
import { PipelineOverviewObjectsConfiguration } from './PipelineOverviewObjectsConfiguration/PipelineOverviewObjectsConfiguration';
import { getErrorMessageFromAxiosError } from '../../../request/utils';
import { PipelineOverviewProvider } from '../context/PipelineOverviewContext';
import { usePipeline } from '../context/usePipeline';
import { PIPELINE_OVERVIEW_DATA_ID_CONTRACT } from './pipelineOverviewDataIdContract';
import { PipelineOverviewJobHistory } from './PipelineOverviewJobHistory';

export default function PipelineOverview() {
  const { id } = useParams();

  return (
    <PipelineOverviewProvider pipelineId={Number(id)}>
      <Pipeline />
    </PipelineOverviewProvider>
  );
}

const dataIds = getDataIdsFromContract(PIPELINE_OVERVIEW_DATA_ID_CONTRACT);

function Pipeline() {
  const { pipeline, uiState, error, fetchPipeline } = usePipeline();

  const tabs = useMemo(() => {
    if (!pipeline) {
      return [];
    }

    return [
      {
        label: 'Job History',
        route: '/job-history/*',
        path: `job-history`,
        Component: PipelineOverviewJobHistory,
        icon: 'job-history',
        shortcut: ''
      },
      {
        label: 'Pipeline Setup',
        route: '/configuration',
        path: `configuration`,
        Component: PipelineOverviewV1SetupWrapper,
        icon: 'settings',
        shortcut: ''
      },
      {
        label: 'Objects',
        route: '/objects-configuration',
        path: `objects-configuration`,
        Component: PipelineOverviewObjectsConfiguration,
        icon: 'object-config',
        shortcut: ''
      }
    ];
  }, [pipeline]);

  return (
    <div data-id={dataIds.container} className='main-content-area'>
      {uiState === EntityUIState.LOADING ? (
        <GenericPageShimmer dataId={dataIds.loadingShimmer} className='w-100' />
      ) : null}

      {uiState === EntityUIState.ERRORED && error ? (
        <RetryApiAlert
          title='Failed to load the Pipeline'
          subTitle={getErrorMessageFromAxiosError(error) || 'Retry loading the Pipeline'}
          secondaryCTAProps={{
            onClick: fetchPipeline
          }}
          className='h-100 w-100'
          dataId={dataIds.retryApiAlert}
        />
      ) : null}

      {[EntityUIState.IDLE, EntityUIState.REFRESHING].includes(uiState) && pipeline ? (
        <div className='d-flex h-100'>
          <SecondarySidebar tabs={tabs} />

          <div className='main-content-wrapper flex-1'>
            <div className='main-content-area scrollable-content flex-1'>
              <div className='floating-header-container'>
                <PipelineHeader />
              </div>

              <div className='flex-1'>
                <Routes>
                  {tabs.map(tab => {
                    const TabComponent = tab.Component;

                    return (
                      <Route
                        key={tab.label}
                        path={tab.route}
                        element={<TabComponent integration={pipeline} />}
                      />
                    );
                  })}
                  <Route path='*' element={<Navigate to='job-history' replace />} />
                </Routes>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
